import {
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  Base,
  ClockIcon,
  PersonalizedTheme,
  ReverLoading
} from '@itsrever/design-system'
import { LoadError, PageHeader, SaveBar } from '@/ui/shared'
import { PortalPreview, PreviewDevice } from '@/ui/components/portal-preview'
import {
  useBranding,
  useSaveBranding,
  changedBrandingAttributes
} from '@/application/manage-branding'
import {
  BrandingImage,
  PendingBrandingImages,
  PreparedImage
} from '@/domain/branding'
import { useDialog } from '@/ui/components/dialog/dialog-provider'
import { useAppSelector } from '@/redux/hooks'
import { Roles } from '@/utils'
import { ColorField, ThemeColorField } from './theme-color-field'
import { ImageField } from './image-field'

const INPUT_VARIANTS = ['OUTLINED', 'STANDARD'] as const

// Ranges wide enough for the shapes a portal actually uses; the fallbacks match what the portal
// renders when the attribute is unset, so an untouched slider is not lying about the current look.
const BORDER_RADIUS_RANGE = { min: 0, max: 2, step: 0.05, fallback: 0.5 }
const LOGO_HEIGHT_RANGE = {
  min: 0.5,
  max: 5,
  step: 0.1,
  mobileFallback: 1.5,
  desktopFallback: 2
}

const PREVIEW_DEVICES: PreviewDevice[] = ['mobile', 'desktop']

// The patch-request keys and pending-image names each inspector group owns, so a group can flag
// its own unsaved edits. Exact names: 'background' the image and 'background_text_color' the
// colour belong to different groups.
const SECTION_CHANGES = {
  page: ['title'],
  images: ['background', 'favicon'],
  logo: ['logo', 'logo_height_desktop', 'logo_height_mobile'],
  brand: ['primary', 'background_text_color'],
  shape: ['border_radius', 'input_variant']
} as const

type SectionKey = keyof typeof SECTION_CHANGES

export const CustomTheme = () => {
  const { t } = useTranslation()

  const {
    branding,
    setBranding,
    originalBranding,
    storedImages,
    loadingBranding,
    brandingFailed,
    reloadBranding
  } = useBranding()
  const { callSaveBranding, loadingSaveBranding } = useSaveBranding()

  // Picked but not uploaded. Images obey the same Save button as every other attribute here, so
  // they wait in this state rather than committing the moment a file is chosen.
  const [pendingImages, setPendingImages] = useState<PendingBrandingImages>({})

  const pickImage = (image: BrandingImage, prepared: PreparedImage | null) =>
    setPendingImages((pending) => {
      const next = { ...pending }
      if (prepared) next[image] = prepared
      else delete next[image]
      return next
    })

  const role = useAppSelector(
    (store) => store.userApi.getMe.response.user?.role
  )
  const canEdit = role === Roles.ReadWrite

  const [device, setDevice] = useState<PreviewDevice>('mobile')

  const logoHeight =
    device === 'desktop'
      ? {
          attribute: 'logoHeightDesktop' as const,
          value: branding.logoHeightDesktop,
          fallback: LOGO_HEIGHT_RANGE.desktopFallback
        }
      : {
          attribute: 'logoHeightMobile' as const,
          value: branding.logoHeightMobile,
          fallback: LOGO_HEIGHT_RANGE.mobileFallback
        }

  const changed = [
    ...changedBrandingAttributes(originalBranding, branding),
    ...Object.keys(pendingImages)
  ]
  const sectionChanged = (section: SectionKey) =>
    SECTION_CHANGES[section].some((key) => changed.includes(key))

  // Only the first load replaces the page with a spinner: a later one (another store) would
  // otherwise unmount the inspector and lose every folded group.
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    if (!loadingBranding) setLoaded(true)
  }, [loadingBranding])

  const [folded, setFolded] = useState<Partial<Record<SectionKey, boolean>>>({})
  const toggle = (section: SectionKey) =>
    setFolded((f) => ({ ...f, [section]: !f[section] }))

  const save = useCallback(async () => {
    const saved = await callSaveBranding(
      originalBranding,
      branding,
      pendingImages
    )
    // Kept when the save failed: a picked image lives only in this page's memory, so clearing it
    // would send the merchant back to find every file again.
    if (saved) setPendingImages({})
  }, [callSaveBranding, originalBranding, branding, pendingImages])

  const showDialog = useDialog()

  // Guarded like leaving the page: it drops every picked image as well as every edit.
  const discard = async () => {
    const confirmed = await showDialog({
      title: t('custom_theme.dialog_discard_title'),
      message: t('custom_theme.dialog_discard_description'),
      okButtonText: t('custom_theme.discard_changes')
    })
    if (!confirmed) return
    setBranding(originalBranding)
    setPendingImages({})
  }

  // Empty means "not configured", and the preview components fall back on a nullish value —
  // passing the empty string instead would style them with it.
  const previewTheme = useMemo<PersonalizedTheme>(
    () => ({
      colors: {
        ...(branding.primary && {
          primary: {
            main: branding.primary.main,
            light: branding.primary.light,
            dark: branding.primary.dark,
            contrastText: branding.primary.contrastText
          }
        })
      },
      borderRadius: branding.borderRadius || undefined,
      inputVariant: branding.inputVariant || undefined
    }),
    [branding]
  )

  // The text over the background image, the logo box and the page title sit outside the
  // design-system theme shape, so they travel to the preview separately.
  const previewBranding = useMemo(
    () => ({
      backgroundTextColor: branding.backgroundTextColor || undefined,
      logoHeight: {
        desktop: branding.logoHeightDesktop || undefined,
        mobile: branding.logoHeightMobile || undefined
      },
      title: branding.title || undefined,
      // Only a pending image travels: leaving these empty lets the preview fall back to what is
      // stored, which is what a shopper sees until the upload happens.
      logoUrl: pendingImages.logo?.previewUrl,
      backgroundImageUrl: pendingImages.background?.previewUrl
    }),
    [branding, pendingImages]
  )

  const stageRef = useRef<HTMLDivElement>(null)
  const previewRef = useRef<HTMLDivElement>(null)
  // The phone is life-size at most; the laptop is drawn small so it may grow into the stage.
  // `loaded`, not `loadingBranding`: the stage mounts a render after loading ends, so keying on
  // the flag spends the only re-run while the refs are still null and the fit never happens.
  // A retried load mounts the stage too, when brandingFailed clears.
  const scale = useFitScale(stageRef, previewRef, device === 'mobile' ? 1 : 2, [
    device,
    loaded,
    brandingFailed
  ])

  const inputVariantLabel = t(
    `custom_theme.input_variant_${(branding.inputVariant || 'OUTLINED').toLowerCase()}`
  )
  const storedOrPending = (image: BrandingImage) =>
    Boolean(pendingImages[image]?.previewUrl || storedImages[image])

  const header = (
    <PageHeader title={t('theme.title')} description={t('theme.description')} />
  )

  if (!loaded) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <ReverLoading />
      </div>
    )
  }

  if (brandingFailed) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <div className="px-7 py-4">
          <LoadError
            message={t('custom_theme.load_error')}
            onRetry={reloadBranding}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 min-w-[960px]">
      {/* The stage: the portal is the one thing every attribute is judged on, so it gets the
          room, and the save bar floats under it where the eye lands after a change. */}
      <div className="flex min-w-0 flex-1 flex-col">
        {header}

        {/* Toolbar, device and note stack in normal flow rather than floating over one another,
            so the device can never end up under the toolbar whatever the window size. */}
        <div className="flex min-h-0 flex-1 flex-col bg-[radial-gradient(ellipse_at_50%_40%,#EEF0F4_0%,#F7F7F7_70%)]">
          <div className="flex shrink-0 justify-center pt-5">
            <Segmented
              options={PREVIEW_DEVICES.map((option) => ({
                value: option,
                label: t(`custom_theme.device_${option}`),
                icon: <DeviceIcon device={option} />
              }))}
              value={device}
              onChange={setDevice}
            />
          </div>

          {/* Only the landing page is previewed here: it is the one page every theme attribute
              shows on. Scaled around its centre, so it stays whole on short screens. */}
          <div
            ref={stageRef}
            className="flex min-h-0 flex-1 items-center justify-center overflow-hidden"
          >
            <div
              ref={previewRef}
              style={{ transform: `scale(${scale})` }}
              className="drop-shadow-2xl"
            >
              <PortalPreview
                portalView="landing"
                theme={previewTheme}
                branding={previewBranding}
                device={device}
              />
            </div>
          </div>

          {/* The refetch after a save keeps the diff non-empty until it lands, so it counts as
              saving too or a second Cmd+S in that window saves twice. */}
          <SaveBar
            editable={canEdit}
            changes={changed.length}
            saving={loadingSaveBranding || loadingBranding}
            onDiscard={discard}
            onSave={save}
          />

          <p className="flex shrink-0 items-center justify-center gap-2 pb-5 text-xs text-gray-400">
            <ClockIcon className="size-3.5" />
            {t('custom_theme.applies_on_next_visit')}
          </p>
        </div>
      </div>

      {/* The inspector: every attribute in one scrolling column, grouped by what it affects.
          Groups collapse to a one-line summary so a merchant can fold away what they are done
          with and still read its value. */}
      <aside className="flex w-[360px] shrink-0 flex-col border-l border-gray-200 bg-white">
        <div className="flex h-16 shrink-0 items-center border-b border-gray-200 px-5 text-sm font-semibold text-gray-900">
          {t('custom_theme.inspector_title')}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <InspectorGroup
            title={t('custom_theme.section_page')}
            summary={branding.title || 'Returns'}
            changed={sectionChanged('page')}
            open={!folded.page}
            onToggle={() => toggle('page')}
          >
            <TextField
              id="portal-title"
              label={t('custom_theme.title')}
              hint={t('custom_theme.title_hint')}
              value={branding.title}
              placeholder="Returns"
              disabled={!canEdit}
              onChange={(title) => setBranding({ ...branding, title })}
            />
          </InspectorGroup>

          <InspectorGroup
            title={t('custom_theme.section_images')}
            summary={
              (['background', 'favicon'] as const)
                .filter(storedOrPending)
                .map((image) => t(`custom_theme.image_${image}`))
                .join(' · ') || t('custom_theme.image_none')
            }
            changed={sectionChanged('images')}
            open={!folded.images}
            onToggle={() => toggle('images')}
          >
            <ImageField
              label={t('custom_theme.image_background')}
              hint={t('custom_theme.image_background_hint')}
              image="background"
              storedUrl={storedImages.background}
              pending={pendingImages.background ?? null}
              disabled={!canEdit}
              onPick={(prepared) => pickImage('background', prepared)}
            />
            <ImageField
              label={t('custom_theme.image_favicon')}
              hint={t('custom_theme.image_favicon_hint')}
              image="favicon"
              storedUrl={storedImages.favicon}
              pending={pendingImages.favicon ?? null}
              disabled={!canEdit}
              onPick={(prepared) => pickImage('favicon', prepared)}
            />
          </InspectorGroup>

          <InspectorGroup
            title={t('custom_theme.section_logo')}
            summary={formatRem(
              parseRem(logoHeight.value) ?? logoHeight.fallback
            )}
            changed={sectionChanged('logo')}
            open={!folded.logo}
            onToggle={() => toggle('logo')}
          >
            <ImageField
              label={t('custom_theme.image_logo')}
              hint={t('custom_theme.image_logo_hint')}
              image="logo"
              storedUrl={storedImages.logo}
              pending={pendingImages.logo ?? null}
              disabled={!canEdit}
              onPick={(prepared) => pickImage('logo', prepared)}
            />
            {/* One slider for both devices: it edits the height of whichever one the stage
                shows, so the change is always visible. */}
            <LengthSlider
              id="logo-height"
              label={t(`custom_theme.logo_height_${device}`)}
              hint={t(`custom_theme.logo_height_hint_${device}`)}
              value={logoHeight.value}
              fallback={logoHeight.fallback}
              min={LOGO_HEIGHT_RANGE.min}
              max={LOGO_HEIGHT_RANGE.max}
              step={LOGO_HEIGHT_RANGE.step}
              disabled={!canEdit}
              onChange={(value) =>
                setBranding({ ...branding, [logoHeight.attribute]: value })
              }
            />
          </InspectorGroup>

          <InspectorGroup
            title={t('custom_theme.section_brand')}
            summary={branding.primary?.main ?? t('custom_theme.not_configured')}
            changed={sectionChanged('brand')}
            open={!folded.brand}
            onToggle={() => toggle('brand')}
          >
            <ThemeColorField
              label={t('custom_theme.primary_color')}
              value={branding.primary}
              disabled={!canEdit}
              onChange={(primary) => setBranding({ ...branding, primary })}
            />
            <ColorField
              label={t('custom_theme.background_text_color')}
              hint={t('custom_theme.background_text_color_hint')}
              value={branding.backgroundTextColor}
              disabled={!canEdit}
              onChange={(backgroundTextColor) =>
                setBranding({ ...branding, backgroundTextColor })
              }
            />
          </InspectorGroup>

          <InspectorGroup
            title={t('custom_theme.section_shape')}
            summary={`${formatRem(parseRem(branding.borderRadius) ?? BORDER_RADIUS_RANGE.fallback)} · ${inputVariantLabel}`}
            changed={sectionChanged('shape')}
            open={!folded.shape}
            onToggle={() => toggle('shape')}
          >
            <LengthSlider
              id="border-radius"
              label={t('custom_theme.border_radius')}
              value={branding.borderRadius}
              fallback={BORDER_RADIUS_RANGE.fallback}
              min={BORDER_RADIUS_RANGE.min}
              max={BORDER_RADIUS_RANGE.max}
              step={BORDER_RADIUS_RANGE.step}
              disabled={!canEdit}
              onChange={(borderRadius) =>
                setBranding({ ...branding, borderRadius })
              }
            />
            <div className="flex flex-col gap-2">
              <label className="text-sm text-gray-500">
                {t('custom_theme.input_variant')}
              </label>
              <Segmented
                options={INPUT_VARIANTS.map((variant) => ({
                  value: variant,
                  label: t(
                    `custom_theme.input_variant_${variant.toLowerCase()}`
                  )
                }))}
                value={branding.inputVariant}
                disabled={!canEdit}
                ariaLabel={t('custom_theme.input_variant')}
                onChange={(inputVariant) =>
                  setBranding({ ...branding, inputVariant })
                }
              />
            </div>
          </InspectorGroup>
        </div>
      </aside>
    </div>
  )
}

// The largest scale at which the preview fits the stage with a margin, up to `max`: the phone
// frame is 600px tall, more than a small laptop leaves between the toolbar and the note, while
// the laptop frame is drawn well under the room a wide screen gives it.
function useFitScale(
  stage: RefObject<HTMLDivElement | null>,
  content: RefObject<HTMLDivElement | null>,
  max: number,
  deps: unknown[]
): number {
  const [scale, setScale] = useState(1)

  useEffect(() => {
    const stageEl = stage.current
    const contentEl = content.current
    if (!stageEl || !contentEl) return

    const fit = () => {
      const margin = 88
      const width = (stageEl.clientWidth - margin) / contentEl.offsetWidth
      const height =
        (stageEl.clientHeight - margin * 1.5) / contentEl.offsetHeight
      setScale(Math.max(0.3, Math.min(max, width, height)))
    }
    fit()
    const observer = new ResizeObserver(fit)
    observer.observe(stageEl)
    observer.observe(contentEl)
    return () => observer.disconnect()
    // Re-armed on what the caller says can change the elements; sizes are observed directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [max, ...deps])

  return scale
}

function DeviceIcon({ device }: { device: PreviewDevice }) {
  const common = {
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const
  }
  return device === 'mobile' ? (
    <svg {...common}>
      <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
      <path d="M11 18.5h2" />
    </svg>
  ) : (
    <svg {...common}>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </svg>
  )
}

interface InspectorGroupProps {
  title: string
  // What the group reads as when folded, so a closed group still tells its value.
  summary: string
  // Whether the group holds an unsaved edit; shown as a dot so the change is findable from
  // the group list alone.
  changed: boolean
  open: boolean
  onToggle: () => void
  children: ReactNode
}

// A collapsible group of attributes in the inspector. Folded children stay mounted, hidden, so a
// field's own state (a rejected file, an upload in progress) survives the fold.
function InspectorGroup({
  title,
  summary,
  changed,
  open,
  onToggle,
  children
}: InspectorGroupProps) {
  return (
    <section className="border-b border-gray-200">
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="flex h-11 w-full items-center gap-2 px-5 text-left text-[13px] font-semibold text-gray-900 hover:bg-gray-50"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={
            'text-gray-400 transition-transform ' + (open ? 'rotate-90' : '')
          }
        >
          <path d="m9 6 6 6-6 6" />
        </svg>
        {title}
        {changed && (
          <span
            aria-hidden
            className="inline-block size-1.5 rounded-full bg-primary-main"
          />
        )}
        {!open && (
          <span className="ml-auto truncate pl-3 font-mono text-xs font-normal text-gray-400">
            {summary}
          </span>
        )}
      </button>
      {/* A class rather than the hidden attribute: `flex` would override it. */}
      <div className={open ? 'flex flex-col gap-4 px-5 pb-5 pt-1' : 'hidden'}>
        {children}
      </div>
    </section>
  )
}

interface SegmentedProps<T extends string> {
  options: { value: T; label: string; icon?: ReactNode }[]
  value: string | undefined
  disabled?: boolean
  ariaLabel?: string
  onChange: (value: T) => void
}

// One-of-N as a button row; both selectors on this page take this shape.
function Segmented<T extends string>({
  options,
  value,
  disabled,
  ariaLabel,
  onChange
}: SegmentedProps<T>) {
  return (
    <Base.ToggleGroup
      variant="button"
      aria-label={ariaLabel}
      value={value ? [value] : []}
      disabled={disabled}
      // Pressing the chosen option again reports no value; the choice stays.
      onValueChange={(next) => {
        if (next.length) onChange(next[0] as T)
      }}
    >
      {options.map((option) => (
        <Base.ToggleGroupItem
          key={option.value}
          value={option.value}
          className="inline-flex items-center gap-1.5"
        >
          {option.icon}
          {option.label}
        </Base.ToggleGroupItem>
      ))}
    </Base.ToggleGroup>
  )
}

interface TextFieldProps {
  id: string
  label: string
  hint: string
  value: string
  placeholder: string
  disabled: boolean
  onChange: (value: string) => void
}

// A labelled text attribute with its hint. The hint is what tells a merchant what the value does
// before they see it on the portal.
function TextField({
  id,
  label,
  hint,
  value,
  placeholder,
  disabled,
  onChange
}: TextFieldProps) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm text-gray-500" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        className="w-full rounded-md border border-gray-300 p-2 text-sm disabled:bg-gray-100"
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
      <span className="text-xs text-gray-400">{hint}</span>
    </div>
  )
}

interface LengthSliderProps {
  id: string
  label: string
  hint?: string
  value: string
  // Where the handle sits while the attribute is unset, so the slider starts from what the portal
  // actually renders instead of from zero.
  fallback: number
  min: number
  max: number
  step: number
  disabled: boolean
  onChange: (value: string) => void
}

// A rem length as a slider. Stored values stay CSS strings ('0.4rem'), which is what the portal
// and the API expect — the slider only edits the number in front of the unit.
function LengthSlider({
  id,
  label,
  hint,
  value,
  fallback,
  min,
  max,
  step,
  disabled,
  onChange
}: LengthSliderProps) {
  const { t } = useTranslation()
  const rem = parseRem(value) ?? fallback

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-3">
          <label className="text-sm text-gray-500" htmlFor={id}>
            {label}
          </label>
          {/* Dragging always writes a value, so unsetting the attribute needs its own control. */}
          {value && !disabled && (
            <button
              type="button"
              className="text-xs text-gray-400 underline"
              onClick={() => onChange('')}
            >
              {t('custom_theme.use_default')}
            </button>
          )}
        </div>
        <span className="font-mono text-sm text-gray-700">
          {formatRem(rem)}
        </span>
      </div>
      <input
        id={id}
        type="range"
        className="w-full cursor-pointer accent-primary-main disabled:cursor-not-allowed disabled:accent-gray-400"
        // A value saved before the slider existed can sit outside the range; clamping keeps the
        // handle on the track while the readout still shows what is really stored.
        value={Math.min(Math.max(rem, min), max)}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onChange={(e) => onChange(formatRem(Number(e.target.value)))}
      />
      {hint && <span className="text-xs text-gray-400">{hint}</span>}
    </div>
  )
}

const PX_PER_REM = 16

// Older values were free text, so px and bare numbers still turn up alongside rem.
function parseRem(value: string): number | undefined {
  const match = /^\s*(-?\d*\.?\d+)\s*(rem|px)?\s*$/.exec(value)
  if (!match) return undefined

  const amount = Number(match[1])
  return match[2] === 'px' ? amount / PX_PER_REM : amount
}

function formatRem(rem: number): string {
  return `${Number(rem.toFixed(2))}rem`
}

export default CustomTheme
